import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faPlus, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV } from './nav';

interface Article { id: number; title: string; category: string | null; description: string; }
interface Canned { id: number; title: string; category: string; body: string; use_count: number; }

const KnowledgeBase: React.FC = () => {
    const { api } = useInternalApi();
    const [articles, setArticles] = useState<Article[]>([]);
    const [canned, setCanned] = useState<Canned[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', category: '', body: '' });

    useEffect(() => { document.title = 'Knowledge Base - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('admin-dashboard/support/knowledge-base/');
            if (res.data?.status === 'success') {
                setArticles(res.data.data.articles || []);
                setCanned(res.data.data.canned_responses || []);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.title.trim() || !form.body.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Title and body are required.' });
            return;
        }
        try {
            const res = await api.post('admin-dashboard/support/knowledge-base/', form);
            if (res.data?.status === 'success') {
                setShowCreate(false);
                setForm({ title: '', category: '', body: '' });
                load();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create template.' });
        }
    };

    const deleteCanned = async (c: Canned) => {
        const confirm = await Swal.fire({ title: `Delete "${c.title}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`admin-dashboard/support/canned-responses/${c.id}/`);
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not delete.' });
        }
    };

    const copyBody = (c: Canned) => {
        navigator.clipboard.writeText(c.body);
        Swal.fire({ icon: 'success', title: 'Copied', timer: 900, showConfirmButton: false });
    };

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faBookOpen} className="me-2 text-primary" />Knowledge Base</h2>
                    <p className="text-muted mb-0">Published resources and agent reply templates</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Canned Response</button>
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <div className="row">
                    <div className="col-lg-6 mb-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><strong>Canned Responses</strong> <span className="text-muted small">— insertable from the Ticket Workbench</span></div>
                            <div className="card-body p-0">
                                {canned.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No templates yet.</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Title</th><th>Used</th><th></th></tr></thead>
                                        <tbody>
                                            {canned.map(c => (
                                                <tr key={c.id}>
                                                    <td>
                                                        <strong>{c.title}</strong>
                                                        {c.category && <span className="badge bg-light text-dark ms-2">{c.category}</span>}
                                                        <div className="small text-muted">{c.body.slice(0, 90)}{c.body.length > 90 ? '…' : ''}</div>
                                                    </td>
                                                    <td><span className="badge bg-secondary">{c.use_count}</span></td>
                                                    <td className="text-end">
                                                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => copyBody(c)}><FontAwesomeIcon icon={faCopy} /></button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteCanned(c)}><FontAwesomeIcon icon={faTrash} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6 mb-4">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><strong>Published Resources</strong> <span className="text-muted small">— from the resource library</span></div>
                            <div className="card-body p-0">
                                {articles.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No published resources yet.</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Title</th><th>Category</th></tr></thead>
                                        <tbody>
                                            {articles.map(a => (
                                                <tr key={a.id}>
                                                    <td>{a.title}<div className="small text-muted">{a.description}</div></td>
                                                    <td>{a.category || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Canned Response</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Category</label>
                                    <input className="form-control" placeholder="e.g. technical" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Body</label>
                                    <textarea className="form-control" rows={5} value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default KnowledgeBase;
